// ./api/courseAPI.js

import axiosInstance from '../utilities/axiosInstance';

const courseAPI = {
  // ============================================================================
  // COURSE MANAGEMENT
  // ============================================================================

  // Get all courses
  getAllCourses: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/api/course/all', { params });
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get courses: ${error.message}` };
    }
  },

  // Get published courses
  getPublishedCourses: async () => {
    try {
      const response = await axiosInstance.get('/api/course/published');
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get published courses: ${error.message}` };
    }
  },

  // Search courses
  searchCourses: async (query) => {
    try {
      const response = await axiosInstance.get('/api/course/search', {
        params: { q: query }
      });
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to search courses: ${error.message}` };
    }
  },

  // Create course
  createCourse: async (courseId, courseData) => {
    try {
      const response = await axiosInstance.post(`/api/course/${courseId}`, courseData);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to create course: ${error.message}` };
    }
  },

  // Get course by courseId
  getCourseById: async (courseId) => {
    try {
      const response = await axiosInstance.get(`/api/course/${courseId}`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get course: ${error.message}` };
    }
  },

  // Update course
  updateCourse: async (courseId, updateData) => {
    try {
      const response = await axiosInstance.put(`/api/course/${courseId}`, updateData);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to update course: ${error.message}` };
    }
  },

  // Update course instructor
  updateCourseInstructor: async (courseId, instructorData) => {
    try {
      const response = await axiosInstance.put(
        `/api/course/${courseId}/instructor`,
        instructorData
      );
      return response.data;
    } catch (error) {
      return error?.response?.data || { 
        success: false, 
        error: `Failed to update instructor: ${error.message}` 
      };
    }
  },

  // Remove course instructor
  removeCourseInstructor: async (courseId, removedBy) => {
    try {
      const response = await axiosInstance.delete(
        `/api/course/${courseId}/instructor`,
        { data: { removedBy } }
      );
      return response.data;
    } catch (error) {
      return error?.response?.data || { 
        success: false, 
        error: `Failed to remove instructor: ${error.message}` 
      };
    }
  },

  // Delete course
  deleteCourse: async (courseId) => {
    try {
      const response = await axiosInstance.delete(`/api/course/${courseId}`);
      return response.data; // {success, message}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to delete course: ${error.message}` };
    }
  },

  // Publish course
  publishCourse: async (courseId, publishedById) => {
    console.log("publish course: ", courseId, publishedById);

    try {
      const response = await axiosInstance.put(`/api/course/${courseId}/publish`, {publishedById});
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to publish course: ${error.message}` };
    }
  },

  // Unpublish course
  unpublishCourse: async (courseId, unpublishedById) => {
    console.log("publish course: ", courseId, unpublishedById);
    
    try {
      const response = await axiosInstance.put(`/api/course/${courseId}/unpublish`, {unpublishedById});
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to unpublish course: ${error.message}` };
    }
  },

  // ============================================================================
  // LESSON MANAGEMENT
  // ============================================================================

  // Add lesson to course
  addLesson: async (courseId, lessonId, lessonData) => {
    try {
      const response = await axiosInstance.post(
        `/api/course/${courseId}/lesson/${lessonId}`,
        lessonData
      );
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to add lesson: ${error.message}` };
    }
  },

  // Remove lesson from course
  removeLesson: async (courseId, lessonId) => {
    try {
      const response = await axiosInstance.delete(`/api/course/${courseId}/lesson/${lessonId}`);
      return response.data; // {success, message}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to remove lesson: ${error.message}` };
    }
  },

  // ============================================================================
  // LAB MANAGEMENT
  // ============================================================================

  // Add lab to course
  addLab: async (courseId, labId, labData) => {
    try {
      const response = await axiosInstance.post(
        `/api/course/${courseId}/lab/${labId}`,
        labData
      );
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to add lab: ${error.message}` };
    }
  },

  // Remove lab from course
  removeLab: async (courseId, labId) => {
    try {
      const response = await axiosInstance.delete(`/api/course/${courseId}/lab/${labId}`);
      return response.data; // {success, message}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to remove lab: ${error.message}` };
    }
  },

  // ============================================================================
  // QUIZ MANAGEMENT
  // ============================================================================

  // Add quiz to course
  addQuiz: async (courseId, quizId, quizData) => {
    try {
      const response = await axiosInstance.post(
        `/api/course/${courseId}/quiz/${quizId}`,
        quizData
      );
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to add quiz: ${error.message}` };
    }
  },

  // Remove quiz from course
  removeQuiz: async (courseId, quizId) => {
    try {
      const response = await axiosInstance.delete(`/api/course/${courseId}/quiz/${quizId}`);
      return response.data; // {success, message}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to remove quiz: ${error.message}` };
    }
  },

  // ============================================================================
  // EXAM MANAGEMENT
  // ============================================================================

  // Add exam to course
  addExam: async (courseId, examId, examData) => {
    try {
      const response = await axiosInstance.post(
        `/api/course/${courseId}/exam/${examId}`,
        examData
      );
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to add exam: ${error.message}` };
    }
  },

  // Remove exam from course
  removeExam: async (courseId, examId) => {
    try {
      const response = await axiosInstance.delete(`/api/course/${courseId}/exam/${examId}`);
      return response.data; // {success, message}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to remove exam: ${error.message}` };
    }
  },

    // ============================================================================
  // CAPSTONE MANAGEMENT
  // ============================================================================

  // Add capstone to course
  addCapstone: async (courseId, capstoneId, capstoneData) => {
    try {
      const response = await axiosInstance.post(
        `/api/course/${courseId}/capstone/${capstoneId}`,
        capstoneData
      );
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to add capstone: ${error.message}` };
    }
  },

  // Remove capstone from course
  removeCapstone: async (courseId, capstoneId) => {
    try {
      const response = await axiosInstance.delete(`/api/course/${courseId}/capstone/${capstoneId}`);
      return response.data; // {success, message}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to remove capstone: ${error.message}` };
    }
  },

  // ============================================================================
  // UPDATE COURSE CONTENT (Unified)
  // ============================================================================

  updateCourseContent: async (courseId, contentType, contentId, updateData) => {
    try {
      const response = await axiosInstance.put(
        `/api/course/${courseId}/${contentType}/${contentId}`,
        updateData
      );
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to update ${contentType}: ${error.message}` };
    }
  },

  // ============================================================================
  // CATEGORY MANAGEMENT
  // ============================================================================
  // Add category
  addCategory: async (name) => {
    try {
      const response = await axiosInstance.post('/api/course/categories', { name });
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to add category: ${error.message}` };
    }
  },

  // Update category
  updateCategory: async (oldName, newName) => {
    try {
      const response = await axiosInstance.put(`/api/course/categories/${encodeURIComponent(oldName)}`, { newName });
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to update category: ${error.message}` };
    }
  },

  // Delete category
  deleteCategory: async (name) => {
    try {
      const response = await axiosInstance.delete(`/api/course/categories/${encodeURIComponent(name)}`);
      return response.data; // {success, message}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to delete category: ${error.message}` };
    }
  },

  // Get courses by category
  getCoursesByCategory: async (name) => {
    try {
      const response = await axiosInstance.get(`/api/course/categories/${encodeURIComponent(name)}/courses`);
      return response.data; // {success, data, count}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get courses: ${error.message}` };
    }
  },

  // ============================================================================
  // COUPON MANAGEMENT - ADD TO courseAPI.js
  // ============================================================================

  // Add coupon to course
  addCoupon: async (courseId, couponData) => {
    try {
      const response = await axiosInstance.post(`/api/course/${courseId}/coupon`, couponData);
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to add coupon: ${error.message}` };
    }
  },

  // Remove coupon from course
  removeCoupon: async (courseId, couponCode, removedBy) => {
    try {
      const response = await axiosInstance.delete(`/api/course/${courseId}/coupon/${couponCode}`, {
        data: { removedBy }
      });
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to remove coupon: ${error.message}` };
    }
  },

  // Update coupon
  updateCoupon: async (courseId, couponCode, updates, updatedBy) => {
    try {
      const response = await axiosInstance.put(`/api/course/${courseId}/coupon/${couponCode}`, {
        updates,
        updatedBy
      });
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to update coupon: ${error.message}` };
    }
  },

  // Validate coupon
  validateCoupon: async (courseId, couponCode) => {
    try {
      const response = await axiosInstance.get(`/api/course/${courseId}/coupon/${couponCode}/validate`);
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to validate coupon: ${error.message}` };
    }
  },

  // Calculate discounted price
  calculateDiscountedPrice: async (courseId, couponCode) => {
    try {
      const response = await axiosInstance.get(`/api/course/${courseId}/coupon/${couponCode}/calculate`);
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to calculate price: ${error.message}` };
    }
  },

  // Get CourseDomains config from backend
  getCourseDomains: async () => {
    try {
      const response = await axiosInstance.get('/api/course/domains');
      return response.data; // { success, data }
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get course domains: ${error.message}` };
    }
  },

};

export default courseAPI;